/** @jsxImportSource @emotion/react */
import { TableBody } from "../../../../components/table-components/TableBody";
import { Table } from "../../../../components/table-components/Table";
import { TableRowHeader } from "../../../../components/table-components/TableRowHeader";
import { TableCellHeaderText } from "../../../../components/table-components/TableCellHeaderText";
import { colors } from "../../../../components/colors";
import { TableRow } from "../../../../components/table-components/TableRow";
import { TableCellLink } from "../../../../components/table-components/TableCellLink";
import { AutoCampaignListItem } from "../models/AutoCampaign";
import { routes } from "../../../../appRoutingHelpers";
import { useSelector } from "react-redux";
import * as authSelectors from "../../../auth/authSelectors";
import { StyledText } from "../../../../assets/typography/typography";
import { TableEllipsisDropdown } from "../../../../components/TableEllipsisDropdown";
import { getErrorMessage } from "../../../../error-handler";
import { toastr } from "react-redux-toastr";
import { useDeleteCampaign } from "../service/campaign-api";
import { DropdownItemLink } from "../../../common/DropdownItemLink";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DropdownItemButton } from "../../../common/DropdownItemButton";
import {
  formatDateWithYear,
  formatMoneyAmount,
  getAutoCampaignStatus,
} from "../../../../util";
import { DiscountTypeBadge } from "./DiscountTypeBadge";
import { EnabledBadge } from "./CampaignStatusBadge";

type Props = {
  campaigns: AutoCampaignListItem[];
};

export const CampaignsTable = ({ campaigns }: Props) => {
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );

  const { mutateAsync: deleteCampaign } = useDeleteCampaign();

  return (
    <Table>
      <thead>
        <TableRowHeader>
          <TableCellHeaderText>Admin name</TableCellHeaderText>
          <TableCellHeaderText>Display name</TableCellHeaderText>
          <TableCellHeaderText>Discount type</TableCellHeaderText>
          <TableCellHeaderText>Discount value</TableCellHeaderText>
          <TableCellHeaderText>Active from</TableCellHeaderText>
          <TableCellHeaderText>Active to</TableCellHeaderText>
          <TableCellHeaderText>Campaign status</TableCellHeaderText>
          <th />
        </TableRowHeader>
      </thead>
      <TableBody>
        {campaigns.map((campaign, i) => {
          const href = routes.campaignsEdit(organizationKeyName, campaign.id);

          return (
            <TableRow key={i}>
              <TableCellLink href={href} className="pl-5">
                <StyledText
                  className="bodyTextRegular mb-0"
                  color={colors.textPrimary}
                >
                  {campaign.name}
                </StyledText>
              </TableCellLink>
              <TableCellLink href={href}>
                <StyledText
                  className="bodyTextRegular mb-0"
                  color={colors.textPrimary}
                >
                  {campaign.title}
                </StyledText>
              </TableCellLink>
              <TableCellLink href={href}>
                <DiscountTypeBadge discountType={campaign.discountType} />
              </TableCellLink>
              <TableCellLink href={href}>
                <StyledText
                  className="bodyTextRegular mb-0"
                  color={colors.textPrimary}
                >
                  {campaign.discountType === "fixed_price"
                    ? formatMoneyAmount(
                        campaign.discountValue,
                        campaign.currency || "DKK"
                      )
                    : `${campaign.discountValue}%`}
                </StyledText>
              </TableCellLink>
              <TableCellLink href={href}>
                <StyledText
                  className="bodyTextRegular mb-0"
                  color={colors.textPrimary}
                >
                  {formatDateWithYear(campaign.activeFrom)}
                </StyledText>
              </TableCellLink>
              <TableCellLink href={href}>
                <StyledText
                  className="bodyTextRegular mb-0"
                  color={colors.textPrimary}
                >
                  {formatDateWithYear(campaign.activeTo)}
                </StyledText>
              </TableCellLink>
              <TableCellLink href={href}>
                <EnabledBadge status={getAutoCampaignStatus(campaign)} />
              </TableCellLink>
              <TableEllipsisDropdown
                menu={{
                  items: [
                    {
                      key: "1",
                      label: (
                        <DropdownItemLink
                          icon={<FontAwesomeIcon icon={solid("pen")} />}
                          to={href}
                        >
                          Edit
                        </DropdownItemLink>
                      ),
                    },
                    {
                      key: "2",
                      label: (
                        <DropdownItemButton
                          icon={<FontAwesomeIcon icon={solid("trash")} />}
                          onClick={async () => {
                            try {
                              await deleteCampaign({
                                organizationKeyName,
                                data: {
                                  id: campaign.id,
                                },
                              });
                              toastr.success("Success", "Campaign deleted");
                            } catch (error) {
                              const errorMessage = getErrorMessage(error);
                              toastr.error("Error", errorMessage);
                            }
                          }}
                        >
                          Delete
                        </DropdownItemButton>
                      ),
                    },
                  ],
                }}
              />
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};
